import React, { useState, useEffect, useRef } from 'react';
import { Shield, RefreshCw, LogOut, Plus, Copy, Edit, Check, Trash2, FileText, KeyRound, FolderTree } from 'lucide-react';
import PasskeyManager from './components/PasskeyManager';
import LoginPage from './components/LoginPage';
import * as OTPAuth from 'otpauth';
import ModalSalesforce from './components/ModalSalesforce';
import EditSalesforceModal from './components/EditSalesforceModal';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from './ThemeContext';
import logoDark from './assets/logo_dark.png';
import logoLight from './assets/logo_light.png';
import { Routes, Route, Navigate } from 'react-router-dom';
import FAQ from './components/FAQ';
import LogsPage from './components/LogsPage';
import FoldersPage from './components/FoldersPage';
import AdminVaultPage from './components/AdminVaultPage';
import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWebSocket } from './WebSocketContext';

const CircularProgress = ({ progress, size = 40 }) => {
  const strokeWidth = 3;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center group">
      <div className="absolute inset-0 bg-blue-100 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-blue-400 dark:text-blue-500 transition-all duration-200"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-medium dark:text-white">
        {Math.round(progress / 100 * 30)}s
      </div>
    </div>
  );
};

const colorMappings = {
  'from-blue-500 to-blue-600': {
    tokenGradient: 'from-gray-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-700/50',
    pulseColor: 'via-blue-50/10 dark:via-blue-800/10'
  },
  'from-red-500 to-red-600': {
    tokenGradient: 'from-gray-50/50 to-red-50/50 dark:from-gray-800/50 dark:to-gray-700/50',
    pulseColor: 'via-red-50/10 dark:via-red-800/10'
  },
  'from-emerald-500 to-emerald-600': {
    tokenGradient: 'from-gray-50/50 to-emerald-50/50 dark:from-gray-800/50 dark:to-gray-700/50',
    pulseColor: 'via-emerald-50/10 dark:via-emerald-800/10'
  },
  'from-purple-500 to-purple-600': {
    tokenGradient: 'from-gray-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-700/50',
    pulseColor: 'via-purple-50/10 dark:via-purple-800/10'
  },
  'from-orange-500 to-orange-600': {
    tokenGradient: 'from-gray-50/50 to-orange-50/50 dark:from-gray-800/50 dark:to-gray-700/50',
    pulseColor: 'via-orange-50/10 dark:via-orange-800/10'
  }
};

function App() {
  const [tokens, setTokens] = useState({});
  const [progress, setProgress] = useState(100);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [customServices, setCustomServices] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [userTitle, setUserTitle] = useState('');
  const [userDeliveryIds, setUserDeliveryIds] = useState([]);
  const [userDeliveries, setUserDeliveries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(null);
  const { isDark, toggleTheme } = useTheme();
  const [showPasskeyManager, setShowPasskeyManager] = useState(false);
  const [canManageFolders, setCanManageFolders] = useState(false);
  const [manageAllFolders, setManageAllFolders] = useState(false);
  const socket = useWebSocket();

  // 🔥 NOUVEAU: Refs pour éviter les doublons
  const loggedActionsRef = useRef(new Set());
  const lastSearchRef = useRef('');

  // Fonction pour vérifier si l'utilisateur est admin
  // Super-user codé en dur (DEBUG) : m.zegadi.
  // TODO: à retirer à la fin pour ne garder que le rôle Manager.
  const isAdmin = () => {
    const superUsers = ['m.zegadi@solganeo.com'];
    return userTitle === 'Manager' || superUsers.includes(user?.email);
  };

  // Folder management is open to managers AND active delegates (Phase 3). Ask the
  // vault who this user is; delegates aren't admins but may still manage folders.
  useEffect(() => {
    if (!user?.email) { setCanManageFolders(false); setManageAllFolders(false); return; }
    (async () => {
      try {
        const r = await fetch('/api/passkey-vault/me', { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setCanManageFolders(!!d.canManageFolders); setManageAllFolders(!!d.manageAllFolders); }
      } catch { /* folders optional */ }
    })();
  }, [user]);
  const canOpenFolders = () => isAdmin() || canManageFolders;

  // 🔥 OPTIMISÉ: Fonction pour logger les actions avec protection contre les doublons
  const logAction = async (action, details = {}) => {
    if (!user?.email) return;

    try {
      // Créer un identifiant unique pour cette action
      const actionId = `${action}-${JSON.stringify(details)}-${Date.now()}`;

      // Éviter les doublons en 5 secondes
      const recentActions = Array.from(loggedActionsRef.current).filter(
        id => Date.now() - parseInt(id.split('-').pop()) < 5000
      );

      if (recentActions.some(id => id.startsWith(`${action}-${JSON.stringify(details)}`))) {
        console.log('🔄 Action déjà loggée récemment, ignorée:', action);
        return;
      }

      // Ajouter à la liste des actions récentes
      loggedActionsRef.current.add(actionId);

      // Nettoyer les anciennes actions (garder seulement les 100 dernières)
      if (loggedActionsRef.current.size > 100) {
        const sortedActions = Array.from(loggedActionsRef.current).sort();
        loggedActionsRef.current = new Set(sortedActions.slice(-50));
      }

      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: user.email,
          userName: user.fullName || user.email,
          action,
          details,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          ip: 'CLIENT_IP'
        }),
      });
    } catch (error) {
      console.error('Erreur lors du logging:', error);
    }
  };

  // Fermer la tooltip si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteTooltip && !event.target.closest('.relative')) {
        setShowDeleteTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDeleteTooltip]);

  // Système de déconnexion automatique après inactivité
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
    let inactivityTimer;

    // Fonction pour déconnecter l'utilisateur
    const handleInactivityLogout = () => {
      console.log('🔴 Déconnexion automatique après 24 heures d\'inactivité');

      // Logger la déconnexion par inactivité
      if (user) {
        logAction('LOGOUT', { logoutType: 'inactivity_timeout' });
      }

      handleLogout();
    };

    // Fonction pour réinitialiser le timer d'inactivité
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(handleInactivityLogout, INACTIVITY_TIMEOUT);
    };

    // Événements qui indiquent une activité utilisateur
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Ajouter les listeners d'événements
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Démarrer le timer initial
    resetInactivityTimer();

    // Cleanup
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
    };
  }, [user]);

  // Session serveur expirée (ex. redémarrage du dyno Heroku) alors que le
  // client se croyait connecté : on nettoie et on renvoie vers l'écran de login
  // au lieu d'afficher un dashboard vide.
  const handleSessionExpired = () => {
    console.warn('⚠️ Session expirée — reconnexion nécessaire');
    setUser(null);
    localStorage.removeItem('user');
    setTokens({});
    setCustomServices([]);
    setUserDeliveryIds([]);
    setUserTitle('');
  };

  const loadSalesforceServices = async () => {
    try {
      // D'abord, chargeons tous les services
      console.log('🔄 Chargement des services...');
      const response = await fetch('/api/salesforce', { credentials: 'include' });

      if (response.status === 401) { handleSessionExpired(); return; }
      if (response.ok) {
        const services = await response.json();
        console.log('📦 Tous les services:', services);

        // 🔥 OPTIMISÉ: Logger seulement si les services ont changé
        const currentCount = customServices.length;
        if (services.length !== currentCount) {
          logAction('SERVICES_LOADED', {
            servicesCount: services.length,
            userType: userTitle,
            previousCount: currentCount
          });
        }

        // Pour les managers
        if (userTitle === 'Manager') {
          // Récupérer tous les deliveries sans filtrage
          const deliveryResponse = await fetch(`/api/delivery-details?isManager=true`);
          if (!deliveryResponse.ok) {
            throw new Error('Erreur lors de la récupération des deliveries');
          }

          const deliveries = await deliveryResponse.json();
          console.log('👑 Tous les deliveries (Mode Manager):', deliveries);
          setUserDeliveries(deliveries);

          // Afficher tous les services
          const allServices = services.map(service => ({
            ...service,
            id: 'salesforce-' + service._id,
            name: service.name || 'Non renseigné',
            username: service.username || 'Non renseigné',
            description: service.description || 'Aucune description',
            color: service.color || 'from-blue-500 to-blue-600',
            gradient: service.gradient || 'bg-gradient-to-br from-blue-500/80 to-blue-600/90',
            secret: service.secret || null
          }));
          setCustomServices(allServices);
          return;
        }

        // Pour les non-managers, on continue avec le filtrage par delivery
        const deliveryResponse = await fetch(
          `/api/delivery-details?${new URLSearchParams({
            ids: JSON.stringify(userDeliveryIds),
            isManager: 'false'
          }).toString()}`
        );

        if (!deliveryResponse.ok) {
          throw new Error('Erreur lors de la récupération des deliveries');
        }

        const deliveries = await deliveryResponse.json();
        console.log('🎯 Vos Deliveries:', deliveries.map(delivery => ({
          id: delivery.Id,
          name: delivery.Name
        })));
        setUserDeliveries(deliveries);

        const allowedAuthenticatorIds = new Set();
        deliveries.forEach(delivery => {
          if (delivery.Authenticators__r?.records) {
            delivery.Authenticators__r.records.forEach(auth => {
              allowedAuthenticatorIds.add(auth.Id);
            });
          }
        });

        // Filtrer les services pour les non-managers
        const filteredServices = services.filter(service =>
          allowedAuthenticatorIds.has(service._id)
        ).map(service => ({
          ...service,
          id: 'salesforce-' + service._id,
          name: service.name || 'Non renseigné',
          username: service.username || 'Non renseigné',
          description: service.description || 'Aucune description',
          color: service.color || 'from-blue-500 to-blue-600',
          gradient: service.gradient || 'bg-gradient-to-br from-blue-500/80 to-blue-600/90',
          secret: service.secret || null
        }));

        setCustomServices(filteredServices);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
    }
  };

  const handleLogin = async (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));

    // Logger la connexion
    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userData.email,
          userName: userData.fullName || userData.email,
          action: 'LOGIN',
          details: { loginMethod: 'email_verification' },
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          ip: 'CLIENT_IP'
        }),
      });
    } catch (error) {
      console.error('Erreur lors du logging de connexion:', error);
    }

    try {
      const response = await fetch(`/api/user-deliveries?email=${encodeURIComponent(userData.email)}`);
      if (response.ok) {
        const data = await response.json();
        setUserTitle(data.title || '');
        setUserDeliveryIds(data.deliveryIds || []);

        // Une fois que nous avons le titre et les deliveryIds, charger les services
        // Pour le Manager, on charge directement tous les services
        if (data.title === 'Manager') {
          const servicesResponse = await fetch('/api/salesforce', { credentials: 'include' });
          if (servicesResponse.ok) {
            const services = await servicesResponse.json();
            const allServices = services.map(service => ({
              ...service,
              id: 'salesforce-' + service._id,
              name: service.name || 'Non renseigné',
              username: service.username || 'Non renseigné',
              description: service.description || 'Aucune description',
              color: service.color || 'from-blue-500 to-blue-600',
              gradient: service.gradient || 'bg-gradient-to-br from-blue-500/80 to-blue-600/90',
              secret: service.secret || null
            }));
            setCustomServices(allServices);
          }
        } else {
          // Pour les non-managers, laisser loadSalesforceServices faire le travail
          await loadSalesforceServices();
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du titre:', error);
    }
  };

  const handleAddService = async (service) => {
    setCustomServices(prev => [...prev, service]);
    await loadSalesforceServices();
    // Le log est fait dans ModalSalesforce
  };

  const handleEdit = (service) => {
    console.log('Service à éditer:', service);
    setSelectedService(service);
    setEditModalOpen(true);

    // Logger l'ouverture du modal d'édition
    logAction('EDIT_MODAL_OPENED', {
      serviceName: service.name,
      serviceId: service.id
    });
  };

  const handleUpdate = async (updatedService) => {
    console.log('Service après update:', updatedService);
    await loadSalesforceServices();
    // Le log est fait dans EditSalesforceModal
  };

  const handleDelete = async (service) => {
    if (!isAdmin()) {
      console.log('❌ Seuls les managers peuvent supprimer des services');
      return;
    }

    const isConfirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le service "${service.name}" ?\n\nCette action est irréversible.`
    );

    if (!isConfirmed) return;

    try {
      const salesforceId = service.id.replace('salesforce-', '');
      const response = await fetch(`/api/salesforce/${salesforceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        console.log('✅ Service supprimé avec succès');
        await loadSalesforceServices(); // Recharger la liste

        // Logger la suppression
        logAction('SERVICE_DELETED', {
          serviceName: service.name,
          serviceId: service.id
        });
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du service');
    }
  };

  const handleCopyToken = (serviceId) => {
    const token = tokens[serviceId];
    if (token) {
      navigator.clipboard.writeText(token);
      setCopiedToken(serviceId);
      setTimeout(() => setCopiedToken(null), 2000);

      // Logger la copie
      const service = customServices.find(s => s.id === serviceId);
      if (user && service) {
        logAction('TOKEN_COPIED', {
          serviceName: service.name,
          serviceId: serviceId
        });
      }
    }
  };

  const handleLogout = () => {
    // Logger la déconnexion
    if (user) {
      logAction('LOGOUT', { logoutType: 'manual' });
    }

    setUser(null);
    localStorage.removeItem('user');
    setTokens({});
    setCustomServices([]);
    setSearchQuery('');
    loggedActionsRef.current.clear(); // Nettoyer les actions loggées
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    console.log('👤 User stocké:', storedUser);
    if (storedUser) {
      const user = JSON.parse(storedUser);
      console.log('🔄 Initialisation utilisateur...');
      setUser(user);

      // Nouvelle fonction pour récupérer le titre
      const fetchUserTitle = async () => {
        try {
          const response = await fetch(`/api/user-deliveries?email=${encodeURIComponent(user.email)}`);
          if (response.ok) {
            const data = await response.json();
            setUserTitle(data.title || '');
            setUserDeliveryIds(data.deliveryIds || []);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du titre:', error);
        }
      };

      fetchUserTitle();
    }
  }, []);

  useEffect(() => {
    if (userDeliveryIds.length > 0) {
      loadSalesforceServices();
    }
  }, [userDeliveryIds, userTitle]);

  useEffect(() => {
    const updateTokens = () => {
      setIsRefreshing(true);
      const newTokens = {};

      customServices.forEach(service => {
        if (service.secret) {
          try {
            const totp = new OTPAuth.TOTP({
              secret: service.secret,
              algorithm: 'SHA1',
              digits: 6,
              period: 30
            });
            newTokens[service.id] = totp.generate();
          } catch (error) {
            console.error(`Erreur génération token pour ${service.name}:`, error);
            newTokens[service.id] = 'Non disponible';
          }
        } else {
          newTokens[service.id] = 'Null';
        }
      });

      setTokens(newTokens);
      setProgress(100);
      setTimeout(() => setIsRefreshing(false), 500);
    };

    updateTokens();
    const interval = setInterval(updateTokens, 30000);
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.max(0, prev - (100 / 30)));
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [customServices]);

  useEffect(() => {
    if (socket) {
      socket.on('serviceCreated', async (newService) => {
        console.log('📦 Nouveau service reçu:', newService);
        await loadSalesforceServices(); // Recharge tous les services
      });

      socket.on('serviceUpdated', async (updatedService) => {
        console.log('🔄 Service mis à jour reçu:', updatedService);
        await loadSalesforceServices(); // Recharge tous les services
      });

      return () => {
        socket.off('serviceCreated');
        socket.off('serviceUpdated');
      };
    }
  }, [socket, loadSalesforceServices]);

  // Filtrer les services selon la recherche
  const filteredServices = customServices.filter(service => {
    const searchLower = searchQuery.toLowerCase();
    return (
      service.name.toLowerCase().includes(searchLower) ||
      service.username.toLowerCase().includes(searchLower) ||
      service.description.toLowerCase().includes(searchLower) ||
      (service.delivery && userDeliveries.find(d => d.Id === service.delivery)?.Name.toLowerCase().includes(searchLower))
    );
  });

  useEffect(() => {
    if (searchQuery && user && searchQuery !== lastSearchRef.current) {
      const timeoutId = setTimeout(() => {
        if (searchQuery === lastSearchRef.current) return;

        lastSearchRef.current = searchQuery;

        // Calculer les résultats ici plutôt que d'utiliser filteredServices
        const results = customServices.filter(service => {
          const searchLower = searchQuery.toLowerCase();
          return (
            service.name.toLowerCase().includes(searchLower) ||
            service.username.toLowerCase().includes(searchLower) ||
            service.description.toLowerCase().includes(searchLower) ||
            (service.delivery && userDeliveries.find(d => d.Id === service.delivery)?.Name.toLowerCase().includes(searchLower))
          );
        });

        logAction('SEARCH_PERFORMED', {
          searchQuery: searchQuery,
          resultsCount: results.length
        });
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, user, customServices, userDeliveries]);

  if (!user) {
      return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Routes>
      <Route path="/" element={
        <>
          <ModalSalesforce
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onAdd={handleAddService}
            deliveries={userDeliveries}
            logAction={logAction}
          />

          <EditSalesforceModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            service={selectedService}
            onUpdate={handleUpdate}
            deliveries={userDeliveries}
            logAction={logAction}
          />

          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
            <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
              {/* Header */}
              <div className="mb-8 text-center space-y-3">
                <div className="hidden lg:flex items-center justify-between">
                  {/* Partie gauche : Logo */}
                  <div className="flex-1">
                    <img
                      src={isDark ? logoDark : logoLight}
                      alt="Logo Solganeo"
                      className="w-40 h-auto"
                    />
                  </div>

                  {/* Partie centrale : Titre */}
                  <div className="flex-1 flex justify-center items-center gap-2">
                    <Shield className="w-8 h-8 text-blue-400" />
                    <h1 className="text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-400 whitespace-nowrap">
                      Authentication Dashboard
                    </h1>
                  </div>

                  {/* Partie droite : User Info */}
                  <div className="flex-1 flex justify-end">
                    <div className="flex items-center gap-1">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user?.fullName || user?.email}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.primaryEmail}
                        </p>
                        {userTitle && (
                          <p className="text-xs text-blue-500">
                            {userTitle}
                          </p>
                        )}
                      </div>
                      {/* Bouton FAQ */}
                      <Link
                        to="/faq"
                        className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                        title="FAQ"
                      >
                        <HelpCircle className="w-5 h-5 text-gray-500" />
                      </Link>
                      {/* Bouton Logs pour les admins */}
                      {isAdmin() && (
                        <Link
                          to="/logs"
                          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                          title="Logs"
                        >
                          <FileText className="w-5 h-5 text-gray-500" />
                        </Link>
                      )}
                      {/* Bouton Dossiers pour les managers et délégués */}
                      {canOpenFolders() && (
                        <Link
                          to="/folders"
                          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                          title="Dossiers"
                        >
                          <FolderTree className="w-5 h-5 text-gray-500" />
                        </Link>
                      )}
                      {/* Bouton Mes passkeys */}
                      <button
                        onClick={() => setShowPasskeyManager(true)}
                        className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                        title="Mes passkeys"
                      >
                        <KeyRound className="w-5 h-5 text-gray-500" />
                      </button>
                      <button
                        onClick={handleLogout}
                        className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                        title="Se déconnecter"
                      >
                        <LogOut className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Header mobile */}
                <div className="lg:hidden mb-8 space-y-6 relative pt-8">
                  <div className="absolute top-0 left-0 right-0">
                    <Link
                      to="/faq"
                      className="absolute top-2 left-4 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                      title="FAQ"
                    >
                      <HelpCircle className="w-7 h-7 text-gray-500" />
                    </Link>

                    {/* Bouton Logs pour les admins sur mobile */}
                    {isAdmin() && (
                      <Link
                        to="/logs"
                        className="absolute top-2 left-16 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                        title="Logs"
                      >
                        <FileText className="w-7 h-7 text-gray-500" />
                      </Link>
                    )}
                    {/* Bouton Dossiers pour les managers et délégués sur mobile */}
                    {canOpenFolders() && (
                      <Link
                        to="/folders"
                        className="absolute top-2 left-28 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                        title="Dossiers"
                      >
                        <FolderTree className="w-7 h-7 text-gray-500" />
                      </Link>
                    )}
                    {/* Bouton Mes passkeys sur mobile */}
                    <button
                      onClick={() => setShowPasskeyManager(true)}
                      className="absolute top-2 left-40 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                      title="Mes passkeys"
                    >
                      <KeyRound className="w-7 h-7 text-gray-500" />
                    </button>

                    <button
                      onClick={handleLogout}
                      className="absolute top-2 right-4 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                      title="Se déconnecter"
                    >
                      <LogOut className="w-7 h-7 text-gray-500" />
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <img
                      src={isDark ? logoDark : logoLight}
                      alt="Logo Solganeo"
                      className="w-48 h-auto"
                    />
                  </div>

                  <div className="flex justify-center items-center gap-2">
                    <Shield className="w-8 h-8 text-blue-400" />
                    <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-400 whitespace-nowrap text-[clamp(1.5rem,4vw,2rem)]">
                      Authentication Dashboard
                    </h1>
                  </div>

                  <div className="flex justify-center">
                    <div className="text-center">
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        {user?.fullName || user?.email}
                      </p>
                      <p className="text-sm text-gray-500">
                        {user?.primaryEmail}
                      </p>
                      {userTitle && (
                        <p className="text-sm text-blue-500">
                          {userTitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-gray-500 dark:text-gray-400 text-lg [@media(max-width:1023px)]:text-xl">
                  Système de tokens sécurisés
                </p>
                <div className="w-32 h-1 bg-gradient-to-r from-blue-400 to-sky-400 mx-auto rounded-full" />

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  title={isDark ? "Mode clair" : "Mode sombre"}
                >
                  {isDark ? (
                    <Sun className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>

              {/* Search Bar */}
              <div className="mb-8">
                <div className="relative w-full max-w-2xl mx-auto">
                  <input
                    type="text"
                    placeholder="Rechercher un service..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 py-4 pr-14 rounded-2xl border-2 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all duration-200 shadow-lg hover:shadow-xl"
                  />
                  <div className="absolute right-5 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-6 h-6 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
                {searchQuery && filteredServices.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <div className="text-gray-400 dark:text-gray-500 text-lg">
                      Aucun service trouvé pour "{searchQuery}"
                    </div>
                  </div>
                ) : (
                  filteredServices.map((service, index) => (
                    <div
                      key={service.id}
                      className="group relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                    >
                      {/* Bouton D pour supprimer */}
                      <div className="absolute top-3 right-3 z-20">
                        <div className="relative">
                          <button
                            onClick={() => setShowDeleteTooltip(showDeleteTooltip === service.id ? null : service.id)}
                            className="w-5 h-5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs font-medium transition-all duration-200 opacity-60 hover:opacity-100"
                            title="Informations de suppression"
                          >
                            D
                          </button>

                          {/* Popup tooltip */}
                          {showDeleteTooltip === service.id && (
                            <div className="absolute top-8 right-0 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-30">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                    Suppression du service
                                  </h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                                    Pour supprimer cette card, rendez-vous dans <strong>Salesforce</strong> dans la table <strong>Authenticators</strong> et supprimez l'enregistrement correspondant.
                                  </p>
                                  <div className="flex justify-end">
                                    <button
                                      onClick={() => setShowDeleteTooltip(null)}
                                      className="text-sm text-blue-500 hover:text-blue-600 font-medium"
                                    >
                                      Compris
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Flèche pointant vers le bouton */}
                              <div className="absolute -top-2 right-3 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className={`absolute inset-0 ${service.gradient} opacity-5`} />
                      </div>

                      <div className="relative p-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl ${service.gradient} flex items-center justify-center text-white font-bold shadow-lg transform group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                              {service.name[0]}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-700 dark:text-white text-lg">{service.name}</h3>
                                <button
                                  onClick={() => handleEdit(service)}
                                  className="p-1 hover:bg-gray-100/10 dark:hover:bg-gray-800/50 rounded-full transition-colors"
                                  title="Modifier"
                                >
                                  <Edit size={14} className="text-gray-400" />
                                </button>
                                {isAdmin() && (
                                  <button
                                    onClick={() => handleDelete(service)}
                                    className="p-1 hover:bg-red-100/10 dark:hover:bg-red-800/50 rounded-full transition-colors group"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={14} className="text-gray-400 group-hover:text-red-500 transition-colors" />
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-300">{service.username}</p>
                              {service.description && (
                                <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">{service.description}</p>
                              )}
                              {service.delivery && (
                                <p className="text-xs text-indigo-400 mt-1">
                                  {userDeliveries.find(d => d.Id === service.delivery)?.Name || ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <CircularProgress progress={progress} />
                        </div>

                        <div className="relative overflow-hidden rounded-xl">
                          <div className={`text-2xl font-mono font-bold tracking-[0.3em] text-center p-6 bg-gradient-to-r dark:text-white ${colorMappings[service.color]?.tokenGradient || 'from-gray-50 to-blue-50'} border border-gray-200/20 dark:border-gray-700/20 rounded-xl shadow-inner`}>
                            {tokens[service.id]?.split('').join(' ') || '- - - - - -'}
                            <button
                              onClick={() => handleCopyToken(service.id)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors z-10"
                              title="Copier le code"
                            >
                              {copiedToken === service.id ? (
                                <Check size={16} className="text-green-500" />
                              ) : (
                                <Copy size={16} className="text-gray-400" />
                              )}
                            </button>
                          </div>
                          <div className={`absolute inset-0 bg-gradient-to-r from-transparent ${colorMappings[service.color]?.pulseColor || 'via-white/20'} to-transparent animate-pulse`} />
                        </div>

                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Fixed Elements */}
          <div className="fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-t border-gray-100 dark:border-gray-700 p-4 z-40">
            <div className="w-full max-w-7xl mx-auto flex justify-between items-center">
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 border border-gray-100 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-700 hover:shadow-xl transition-all duration-200"
              >
                <Plus className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-white">Ajouter</span>
              </button>

              <div className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 border border-gray-100 dark:border-gray-700">
                <RefreshCw className={`w-5 h-5 text-blue-400 ${isRefreshing ? 'animate-spin' : ''}`} />
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-400 to-sky-400 rounded-full transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium min-w-[3ch] text-gray-500">
                    {Math.round((progress / 100) * 30)}s
                  </span>
                </div>
              </div>
            </div>
          </div>

          <PasskeyManager
            isOpen={showPasskeyManager}
            onClose={() => setShowPasskeyManager(false)}
          />
        </>
      } />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/folders" element={
        canOpenFolders() ? <FoldersPage isManager={isAdmin()} manageAll={manageAllFolders} /> : <Navigate to="/" replace />
      } />
      <Route path="/logs" element={
        isAdmin() ? <LogsPage /> : <Navigate to="/" replace />
      } />
      {/* Manager-only "everything" view of the vault (Phase 4). */}
      <Route path="/vault" element={
        isAdmin() ? <AdminVaultPage /> : <Navigate to="/" replace />
      } />
    </Routes>
  );
}

export default App;